"use client";

import { useEffect, useMemo, useState } from "react";
import { Frame, Verdict, Note } from "./parts";
import SolarIcon from "@/components/ui/SolarIcon/SolarIcon";

const L = {
    fr: {
        title: "Composez un programme, la boucle choisit un autre ordre",
        add: "Ajouter une ligne :",
        reset: "Revenir à l'exemple",
        remove: "Retirer cette ligne",
        program: "Votre programme",
        outRank: "Rang de sortie",
        stack: "Pile d'appels",
        microQ: "Microtâches",
        taskQ: "Tâches",
        empty: "vide",
        console: "Console",
        written: "Ordre d'écriture :",
        step: "Étape",
        prev: "Étape précédente",
        next: "Étape suivante",
        play: "Dérouler la boucle",
        pause: "Mettre en pause",
        moved: "Lignes déplacées :",
        of: "sur",
        shift: "Écart maximal :",
        rank1: "rang",
        rankN: "rangs",
        stepStart: "Rien n'a encore tourné. La pile est vide, les deux files aussi.",
        stepSync: "Ligne {n} : la pile exécute {l}, la console l'affiche aussitôt.",
        stepTask: "Ligne {n} : setTimeout confie {l} à l'environnement. Le délai valant zéro, {l} rejoint tout de suite la file des tâches.",
        stepMicro: "Ligne {n} : la promesse est déjà tenue, sa suite {l} part dans la file des microtâches.",
        stepDrain: "Le script est terminé, la pile est vide. La boucle sert alors les microtâches, et elles seules.",
        stepDrainNoMicro: "Le script est terminé, la pile est vide. Aucune microtâche n'attend, la boucle prend donc une tâche.",
        stepRunMicro: "Microtâche : {l} s'exécute. La file se vide entièrement avant qu'une seule tâche soit servie.",
        stepRunMicroSeul: "Microtâche : {l} s'exécute. La boucle vide cette file en entier avant de regarder ailleurs.",
        stepRunTask: "Tâche : {l} s'exécute. La boucle n'en prend qu'une, puis revient voir les microtâches.",
        stepEnd: "Tout est servi. La console a affiché ce que vous lisez ci-dessous.",
        flipMicro: "L'ordre d'affichage n'est pas l'ordre d'écriture. Le script se déroule d'abord en entier, puis la file des microtâches se vide complètement, et une tâche seulement passe ensuite. Un .then inscrit après un setTimeout s'exécute donc avant lui.",
        flipSync: "L'ordre d'affichage n'est pas l'ordre d'écriture. Le script se déroule d'abord en entier : vos console.log s'affichent tous avant le moindre callback, y compris ceux inscrits plus haut dans le programme. Ajoutez un .then après un setTimeout et vous verrez aussi une microtâche doubler une tâche.",
        same: "Ici les deux ordres coïncident : vos lignes sont écrites dans l'ordre même où la boucle les sert, le synchrone d'abord, puis les microtâches, puis les tâches. Placez un .then après un setTimeout et l'ordre bascule.",
        note: "Tous les setTimeout portent un délai de zéro, les tâches sont donc servies dans leur ordre d'inscription ; avec des délais différents, c'est l'échéance qui décide entre elles. La démo montre le callback rejoindre la file dès son inscription, alors que l'environnement l'y dépose une fois le minuteur écoulé : un délai de zéro vaut en réalité une milliseconde environ, ce qui ne change rien ici puisque le script se termine avant. Sont également laissés de côté le temps d'exécution, le rendu de la page et les phases supplémentaires de Node.js.",
    },
    en: {
        title: "Compose a program, the loop picks a different order",
        add: "Add a line:",
        reset: "Back to the example",
        remove: "Remove this line",
        program: "Your program",
        outRank: "Output rank",
        stack: "Call stack",
        microQ: "Microtasks",
        taskQ: "Tasks",
        empty: "empty",
        console: "Console",
        written: "Written order:",
        step: "Step",
        prev: "Previous step",
        next: "Next step",
        play: "Run the loop",
        pause: "Pause",
        moved: "Lines out of place:",
        of: "of",
        shift: "Largest shift:",
        rank1: "position",
        rankN: "positions",
        stepStart: "Nothing has run yet. The stack is empty, and so are both queues.",
        stepSync: "Line {n}: the stack runs {l}, and the console prints it right away.",
        stepTask: "Line {n}: setTimeout hands {l} to the environment. The delay being zero, {l} joins the task queue at once.",
        stepMicro: "Line {n}: the promise is already settled, so its continuation {l} goes into the microtask queue.",
        stepDrain: "The script is done and the stack is empty. The loop now serves microtasks, and nothing else.",
        stepDrainNoMicro: "The script is done and the stack is empty. No microtask is waiting, so the loop picks up a task.",
        stepRunMicro: "Microtask: {l} runs. The queue drains completely before a single task is served.",
        stepRunMicroSeul: "Microtask: {l} runs. The loop drains this queue entirely before it looks anywhere else.",
        stepRunTask: "Task: {l} runs. The loop takes only one, then goes back to the microtasks.",
        stepEnd: "Everything has been served. The console printed what you can read below.",
        flipMicro: "The printed order is not the written order. The script runs to the end first, then the microtask queue drains completely, and only then does one task get its turn. A .then registered after a setTimeout therefore runs before it.",
        flipSync: "The printed order is not the written order. The script runs to the end first: every console.log prints before any callback at all, including the callbacks registered further up the program. Add a .then after a setTimeout and you will also see a microtask overtake a task.",
        same: "Here both orders agree: your lines happen to be written in the very order the loop serves them, synchronous first, then microtasks, then tasks. Put a .then after a setTimeout and the order flips.",
        note: "Every setTimeout carries a zero delay, so the tasks are served in registration order; with different delays, the deadline decides between them. The demo shows the callback joining the queue the moment it is registered, whereas the environment only drops it there once the timer has run out: a zero delay really means about a millisecond, which changes nothing here since the script finishes first. Execution time, page rendering and the extra phases of Node.js are left out as well.",
    },
};

const LETTRES = ["A", "B", "C", "D", "E", "F", "G", "H"];
const MAX = LETTRES.length;

// Le programme du terme, à la ligne près : un log, un setTimeout de zéro, un
// then, un log. Il sort dans l'ordre A D C B.
const DEFAUT = ["sync", "task", "micro", "sync"];

const TON = {
    sync: {
        code: "text-gray-600 dark:text-lightBlue",
        chip: "bg-gray-100 text-gray-600 dark:bg-white/[0.08] dark:text-lightBlue",
    },
    micro: {
        code: "text-primary",
        chip: "bg-primary/10 text-primary dark:bg-primary/20",
    },
    task: {
        code: "text-blue-600 dark:text-blue-300",
        chip: "bg-blue-500/10 text-blue-600 dark:bg-blue-400/15 dark:text-blue-300",
    },
};

const codeDe = (genre, lettre) =>
    genre === "sync"
        ? `console.log("${lettre}");`
        : genre === "task"
          ? `setTimeout(() => console.log("${lettre}"), 0);`
          : `Promise.resolve().then(() => console.log("${lettre}"));`;

const remplir = (gabarit, lettre, ligne) =>
    gabarit.split("{l}").join(lettre).split("{n}").join(String(ligne));

const rang = (n, lang) => {
    if (lang !== "en") return n === 1 ? "1re" : `${n}e`;
    const suffixes = ["th", "st", "nd", "rd"];
    const v = n % 100;
    return `${n}${suffixes[(v - 20) % 10] || suffixes[v] || suffixes[0]}`;
};

/**
 * Déroule la boucle d'événements sur le programme composé et retourne une
 * étape par mouvement : contenu de la pile, des deux files et de la console.
 *
 * Le modèle a été confronté à un vrai moteur : les six programmes testés
 * (dont un de huit lignes mélangées) donnent la même sortie sous Node.js que
 * celle calculée ici. Trois règles suffisent, et ce sont celles du terme :
 * le script se déroule en entier, la file des microtâches se vide
 * complètement, puis une tâche seulement est servie avant de recommencer.
 */
function construireTrace(programme, t) {
    const etapes = [];
    let pile = [];
    let micro = [];
    let taches = [];
    let sortie = [];

    const noter = (texte, ligne = null) =>
        etapes.push({
            texte,
            ligne,
            pile: [...pile],
            micro: [...micro],
            taches: [...taches],
            sortie: [...sortie],
        });

    noter(t.stepStart);

    programme.forEach((genre, i) => {
        const lettre = LETTRES[i];
        const jeton = { lettre, genre, ligne: i + 1 };
        if (genre === "sync") {
            pile = ["script", `log("${lettre}")`];
            sortie = [...sortie, jeton];
            noter(remplir(t.stepSync, lettre, i + 1), i + 1);
        } else if (genre === "task") {
            pile = ["script", "setTimeout"];
            taches = [...taches, jeton];
            noter(remplir(t.stepTask, lettre, i + 1), i + 1);
        } else {
            pile = ["script", ".then"];
            micro = [...micro, jeton];
            noter(remplir(t.stepMicro, lettre, i + 1), i + 1);
        }
    });

    pile = [];
    if (micro.length > 0) noter(t.stepDrain);
    else if (taches.length > 0) noter(t.stepDrainNoMicro);

    while (micro.length > 0 || taches.length > 0) {
        while (micro.length > 0) {
            const [tete, ...reste] = micro;
            micro = reste;
            pile = [`log("${tete.lettre}")`];
            sortie = [...sortie, tete];
            noter(
                remplir(
                    taches.length > 0 ? t.stepRunMicro : t.stepRunMicroSeul,
                    tete.lettre,
                    tete.ligne,
                ),
                tete.ligne,
            );
        }
        if (taches.length > 0) {
            const [tete, ...reste] = taches;
            taches = reste;
            pile = [`log("${tete.lettre}")`];
            sortie = [...sortie, tete];
            noter(remplir(t.stepRunTask, tete.lettre, tete.ligne), tete.ligne);
        }
    }

    pile = [];
    noter(t.stepEnd);

    return etapes;
}

export default function EventLoopOrder({ lang = "fr" }) {
    const t = L[lang] || L.fr;
    const [programme, setProgramme] = useState(DEFAUT);
    const [index, setIndex] = useState(0);
    const [lecture, setLecture] = useState(false);

    const trace = useMemo(() => construireTrace(programme, t), [programme, t]);
    const dernier = trace.length - 1;
    const etape = trace[Math.min(index, dernier)];
    const finale = trace[dernier].sortie;

    // Rang de sortie de chaque ligne, et écart avec son rang d'écriture.
    const rangSortie = {};
    finale.forEach((jeton, k) => {
        rangSortie[jeton.ligne] = k + 1;
    });
    const deplacees = finale.filter((jeton, k) => jeton.ligne !== k + 1).length;
    const ecartMax = finale.reduce(
        (m, jeton, k) => Math.max(m, Math.abs(jeton.ligne - (k + 1))),
        0,
    );

    // Le verdict doit nommer la cause présente dans CE programme. Un ordre
    // bascule pour deux raisons : une microtâche inscrite après une tâche la
    // double, ou une ligne synchrone inscrite après un callback passe devant
    // lui. Sur 86 des programmes de quatre lignes qui basculent, 45 n'ont pas
    // de microtâche après tâche : leur annoncer un .then qui double un
    // setTimeout expliquerait un mécanisme absent du code affiché.
    const microApresTache = programme.some(
        (genre, i) => genre === "micro" && programme.slice(0, i).includes("task"),
    );
    // Le français garde le singulier à zéro, l'anglais non.
    const ecartPluriel = lang === "en" ? ecartMax !== 1 : ecartMax > 1;

    useEffect(() => {
        if (!lecture) return undefined;
        if (index >= dernier) {
            setLecture(false);
            return undefined;
        }
        const id = setTimeout(() => setIndex((i) => i + 1), 850);
        return () => clearTimeout(id);
    }, [lecture, index, dernier]);

    const ajouter = (genre) => {
        if (programme.length >= MAX) return;
        setProgramme([...programme, genre]);
        setIndex(0);
        setLecture(false);
    };

    const retirer = (i) => {
        if (programme.length <= 1) return;
        setProgramme(programme.filter((_, k) => k !== i));
        setIndex(0);
        setLecture(false);
    };

    const reinitialiser = () => {
        setProgramme(DEFAUT);
        setIndex(0);
        setLecture(false);
    };

    const basculerLecture = () => {
        if (lecture) {
            setLecture(false);
            return;
        }
        if (index >= dernier) setIndex(0);
        setLecture(true);
    };

    const boutonRond = (icone, libelle, onClick, desactive) => (
        <button
            type="button"
            onClick={onClick}
            disabled={desactive}
            aria-label={libelle}
            title={libelle}
            className={`w-8 h-8 shrink-0 rounded-full border border-gray-200 dark:border-white/10 flex items-center justify-center transition-colors duration-200 ${
                desactive
                    ? "opacity-30 cursor-not-allowed"
                    : "hover:bg-gray-100 dark:hover:bg-white/[0.08]"
            }`}
        >
            <SolarIcon name={icone} className="w-4 h-4 text-gray-600 dark:text-lightBlue" />
        </button>
    );

    const boite = (largeur, titre, contenu) => (
        <div
            className={`${largeur} rounded-xl border border-gray-200/70 dark:border-white/10 p-2 min-h-[78px]`}
        >
            <div className="text-[10px] uppercase tracking-wider text-gray-400 dark:text-grayDark-500 mb-1.5 leading-tight">
                {titre}
            </div>
            {contenu}
        </div>
    );

    const vide = (
        <span className="text-[11px] text-gray-300 dark:text-white/25">{t.empty}</span>
    );

    const jetons = (liste) =>
        liste.length === 0 ? (
            vide
        ) : (
            <div className="flex flex-wrap gap-1">
                {liste.map((jeton) => (
                    <span
                        key={jeton.ligne}
                        className={`w-5 h-5 rounded font-geistMono text-[11px] font-bold flex items-center justify-center ${TON[jeton.genre].chip}`}
                    >
                        {jeton.lettre}
                    </span>
                ))}
            </div>
        );

    return (
        <Frame icon="refresh-linear" title={t.title}>
            <div className="flex flex-wrap items-center gap-2">
                <span className="text-xs text-gray-400 dark:text-grayDark-500">{t.add}</span>
                {["sync", "task", "micro"].map((genre) => (
                    <button
                        key={genre}
                        type="button"
                        onClick={() => ajouter(genre)}
                        disabled={programme.length >= MAX}
                        className={`rounded-full bg-gray-100 dark:bg-white/[0.06] px-2.5 py-1 font-geistMono text-[11px] transition-colors duration-200 ${
                            programme.length >= MAX
                                ? "opacity-30 cursor-not-allowed"
                                : "hover:bg-gray-200 dark:hover:bg-white/10"
                        } ${TON[genre].code}`}
                    >
                        {genre === "sync"
                            ? "console.log()"
                            : genre === "micro"
                              ? ".then()"
                              : "setTimeout()"}
                    </button>
                ))}
                <button
                    type="button"
                    onClick={reinitialiser}
                    aria-label={t.reset}
                    title={t.reset}
                    className="ml-auto flex items-center gap-1 text-xs text-gray-400 dark:text-grayDark-500 hover:text-gray-600 dark:hover:text-lightBlue transition-colors duration-200"
                >
                    <SolarIcon name="restart-linear" className="w-3.5 h-3.5" />
                </button>
            </div>

            <div className="mt-4 flex items-baseline justify-between">
                <span className="text-[10px] uppercase tracking-wider text-gray-400 dark:text-grayDark-500">
                    {t.program}
                </span>
                <span className="text-[10px] uppercase tracking-wider text-gray-400 dark:text-grayDark-500 pr-7">
                    {t.outRank}
                </span>
            </div>

            <div className="mt-1 rounded-xl bg-gray-50 dark:bg-white/[0.04] py-2 px-1">
                {programme.map((genre, i) => {
                    const lettre = LETTRES[i];
                    const sortRang = rangSortie[i + 1];
                    const bouge = sortRang !== i + 1;
                    return (
                        <div
                            key={`${genre}-${i}`}
                            className={`flex items-start gap-2 rounded-lg px-1.5 py-1 transition-colors duration-200 ${
                                etape.ligne === i + 1 ? "bg-primary/[0.07] dark:bg-primary/[0.12]" : ""
                            }`}
                        >
                            <span className="w-3 shrink-0 text-right font-geistMono text-[11px] leading-5 text-gray-300 dark:text-white/25">
                                {i + 1}
                            </span>
                            <code
                                className={`grow font-geistMono text-[11px] sm:text-xs leading-5 break-words ${TON[genre].code}`}
                            >
                                {codeDe(genre, lettre)}
                            </code>
                            <span
                                className={`shrink-0 font-geistMono text-[11px] leading-5 tabular-nums ${
                                    bouge ? "text-primary font-bold" : "text-gray-300 dark:text-white/25"
                                }`}
                            >
                                → {rang(sortRang, lang)}
                            </span>
                            <button
                                type="button"
                                onClick={() => retirer(i)}
                                disabled={programme.length <= 1}
                                aria-label={t.remove}
                                title={t.remove}
                                className={`shrink-0 mt-0.5 ${
                                    programme.length <= 1
                                        ? "opacity-20 cursor-not-allowed"
                                        : "opacity-40 hover:opacity-100 transition-opacity duration-200"
                                }`}
                            >
                                <SolarIcon
                                    name="close-circle-linear"
                                    className="w-3.5 h-3.5 text-gray-500 dark:text-lightBlue"
                                />
                            </button>
                        </div>
                    );
                })}
            </div>

            <div className="mt-6 pt-5 border-t border-gray-200/70 dark:border-white/10">
                <div className="flex items-center gap-2">
                    {boutonRond("alt-arrow-left-linear", t.prev, () => {
                        setLecture(false);
                        setIndex(Math.max(0, index - 1));
                    }, index === 0)}
                    {boutonRond(
                        lecture ? "pause-bold" : "play-bold",
                        lecture ? t.pause : t.play,
                        basculerLecture,
                        false,
                    )}
                    {boutonRond("alt-arrow-right-linear", t.next, () => {
                        setLecture(false);
                        setIndex(Math.min(dernier, index + 1));
                    }, index >= dernier)}
                    <span className="ml-auto text-xs text-gray-400 dark:text-grayDark-500 tabular-nums">
                        {t.step} {Math.min(index, dernier) + 1} / {trace.length}
                    </span>
                </div>

                <div className="mt-3 mb-4 text-sm text-gray-600 dark:text-lightBlue min-h-[56px] sm:min-h-[40px]">
                    {etape.texte}
                </div>

                <div className="grid grid-cols-12 gap-2">
                    {boite(
                        "col-span-5",
                        t.stack,
                        etape.pile.length === 0 ? (
                            vide
                        ) : (
                            <div className="space-y-1">
                                {[...etape.pile].reverse().map((cadre, i) => (
                                    <div
                                        key={i}
                                        className="rounded-md bg-gray-100 dark:bg-white/[0.08] px-1.5 py-0.5 font-geistMono text-[11px] leading-5 text-gray-600 dark:text-lightBlue truncate"
                                    >
                                        {cadre}
                                    </div>
                                ))}
                            </div>
                        ),
                    )}
                    {boite("col-span-4", t.microQ, jetons(etape.micro))}
                    {boite("col-span-3", t.taskQ, jetons(etape.taches))}
                </div>

                <div className="mt-4">
                    <div className="text-[10px] uppercase tracking-wider text-gray-400 dark:text-grayDark-500 mb-1.5">
                        {t.console}
                    </div>
                    <div className="flex flex-wrap gap-1.5">
                        {finale.map((jeton, k) => (
                            <span
                                key={jeton.ligne}
                                className={`w-7 h-7 rounded-lg font-geistMono text-sm font-bold flex items-center justify-center transition-opacity duration-200 ${
                                    TON[jeton.genre].chip
                                } ${k < etape.sortie.length ? "opacity-100" : "opacity-25"}`}
                            >
                                {jeton.lettre}
                            </span>
                        ))}
                    </div>
                    <div className="mt-2 font-geistMono text-[11px] text-gray-400 dark:text-grayDark-500">
                        {t.written} {programme.map((_, i) => LETTRES[i]).join(" ")}
                    </div>
                </div>
            </div>

            <div className="mt-4 flex flex-wrap gap-x-6 gap-y-1 text-sm text-gray-500 dark:text-lightBlue/80">
                <span>
                    {t.moved}{" "}
                    <span className="font-bold tabular-nums dark:text-white">
                        {deplacees} {t.of} {finale.length}
                    </span>
                </span>
                <span>
                    {t.shift}{" "}
                    <span className="font-bold tabular-nums dark:text-white">
                        {ecartMax} {ecartPluriel ? t.rankN : t.rank1}
                    </span>
                </span>
            </div>

            <Verdict tone={deplacees > 0 ? "warn" : "neutral"}>
                {deplacees === 0
                    ? t.same
                    : microApresTache
                      ? t.flipMicro
                      : t.flipSync}
            </Verdict>
            <Note>{t.note}</Note>
        </Frame>
    );
}
